import { cn } from '@/lib/utils'
import type { SchemaField } from '../types'

// ── Fuzzy matching ────────────────────────────────────────────────────────────

function normalize(s: string): string {
  return s.toLowerCase().replace(/[._\-\s]+/g, ' ').trim()
}

function tokenize(s: string): Set<string> {
  return new Set(normalize(s).split(' ').filter(Boolean))
}

function dice(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 1
  if (a.size === 0 || b.size === 0) return 0
  let intersection = 0
  for (const t of a) { if (b.has(t)) intersection++ }
  return (2 * intersection) / (a.size + b.size)
}

function bestMatch(csvHeader: string, schemaFields: SchemaField[]): string {
  const normHeader = normalize(csvHeader)
  const tokensHeader = tokenize(csvHeader)
  let best = { field: '', score: 0 }

  for (const { name } of schemaFields) {
    const normField = normalize(name)
    const tokensField = tokenize(name)

    let score = 0
    if (normHeader === normField) {
      score = 1.0
    } else if (normHeader.includes(normField) || normField.includes(normHeader)) {
      score = 0.75
    } else {
      score = dice(tokensHeader, tokensField)
    }

    if (score > best.score) {
      best = { field: name, score }
    }
  }

  return best.score >= 0.4 ? best.field : ''
}

// ── Groups schema fields by the nested object prefix (before the first '.')
// e.g., "contact.email" → group "contact", "txn_date" → group ""
function groupFields(fields: SchemaField[]): Map<string, SchemaField[]> {
  const groups = new Map<string, SchemaField[]>()
  for (const f of fields) {
    const dot = f.name.indexOf('.')
    const group = dot > -1 ? f.name.substring(0, dot) : ''
    if (!groups.has(group)) groups.set(group, [])
    groups.get(group)!.push(f)
  }
  return groups
}

function GroupedOptions({ fields }: { fields: SchemaField[] }) {
  const groups = groupFields(fields)
  const topLevel = groups.get('') ?? []
  const nested = [...groups.entries()].filter(([g]) => g !== '').sort(([a], [b]) => a.localeCompare(b))

  return (
    <>
      {topLevel.map((f) => (
        <option key={f.name} value={f.name}>
          {f.name}{f.type ? ` (${f.type})` : ''}
        </option>
      ))}
      {nested.map(([group, groupFields]) => (
        <optgroup key={group} label={group}>
          {groupFields.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}{f.type ? ` (${f.type})` : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  )
}

export interface MappingRow {
  csvHeader: string
  schemaField: string
  isUni: boolean
  uniValue: string
  isCustomerMapping: boolean
  customerField: string
}

interface Props {
  csvHeaders: string[]
  schemaFields: SchemaField[]
  customerFields?: SchemaField[]   // populated when showCustomerMapping=true
  showCustomerMapping?: boolean    // entity content type only
  value: MappingRow[]
  onChange: (rows: MappingRow[]) => void
}

export function FieldMappingTable({
  csvHeaders,
  schemaFields,
  customerFields = [],
  showCustomerMapping = false,
  value,
  onChange,
}: Props) {
  if (csvHeaders.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
        No headers yet — upload a file in Step 2 or use "Detect Headers" above.
      </div>
    )
  }

  const getRow = (header: string): MappingRow =>
    value.find((r) => r.csvHeader === header) ?? {
      csvHeader: header,
      schemaField: '',
      isUni: false,
      uniValue: '',
      isCustomerMapping: false,
      customerField: '',
    }

  const updateRow = (header: string, patch: Partial<MappingRow>) => {
    const existing = getRow(header)
    const updated = { ...existing, ...patch }
    const filtered = value.filter((r) => r.csvHeader !== header)
    onChange([...filtered, updated])
  }

  const mappedCount = value.filter((r) => r.schemaField).length
  const uniCount = value.filter((r) => r.isUni).length

  const handleAutoMap = () => {
    const newRows = csvHeaders.map((header) => {
      const existing = value.find((r) => r.csvHeader === header)
      // Don't overwrite already-mapped rows
      if (existing?.schemaField) return existing
      const matched = bestMatch(header, schemaFields)
      return {
        csvHeader: header,
        schemaField: matched,
        isUni: existing?.isUni ?? false,
        uniValue: existing?.uniValue ?? '',
        isCustomerMapping: existing?.isCustomerMapping ?? false,
        customerField: existing?.customerField ?? '',
      }
    })
    onChange(newRows)
  }

  const handleAutoMapCustomer = () => {
    const newRows = csvHeaders.map((header) => {
      const existing = value.find((r) => r.csvHeader === header) ?? {
        csvHeader: header, schemaField: '', isUni: false, uniValue: '', isCustomerMapping: false, customerField: '',
      }
      // Don't overwrite rows that already have a customer field mapped
      if (existing.customerField) return existing
      const matched = bestMatch(header, customerFields)
      if (!matched) return existing
      return { ...existing, isCustomerMapping: true, customerField: matched }
    })
    onChange(newRows)
  }

  const autoMappableCount = csvHeaders.filter((h) => {
    const existing = value.find((r) => r.csvHeader === h)
    if (existing?.schemaField) return false
    return bestMatch(h, schemaFields) !== ''
  }).length

  const autoMappableCustomerCount = showCustomerMapping
    ? csvHeaders.filter((h) => {
        const existing = value.find((r) => r.csvHeader === h)
        if (existing?.customerField) return false
        return bestMatch(h, customerFields) !== ''
      }).length
    : 0

  const colClass = showCustomerMapping
    ? 'grid-cols-[1fr_20px_1.2fr_56px_1fr_56px_1fr]'
    : 'grid-cols-[1fr_20px_1.2fr_56px_1fr]'

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500">
          {mappedCount} of {csvHeaders.length} mapped
          {uniCount > 0 && <span className="ml-2 text-blue-600">{uniCount} uni</span>}
        </p>
        <div className="flex items-center gap-3">
          {autoMappableCount > 0 && (
            <button
              type="button"
              onClick={handleAutoMap}
              className="text-xs font-medium text-primary hover:underline"
            >
              Auto-map {autoMappableCount} field{autoMappableCount !== 1 ? 's' : ''}
            </button>
          )}
          {autoMappableCustomerCount > 0 && (
            <button
              type="button"
              onClick={handleAutoMapCustomer}
              className="text-xs font-medium text-purple-600 hover:underline"
            >
              Auto-map {autoMappableCustomerCount} customer field{autoMappableCustomerCount !== 1 ? 's' : ''}
            </button>
          )}
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-red-500 hover:text-red-600"
          >
            Clear all
          </button>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Header row */}
        <div
          className={cn(
            'grid bg-gray-50 border-b border-gray-200 px-3 py-2 text-xs font-semibold text-gray-500 uppercase gap-2',
            colClass,
          )}
        >
          <span>CSV Header</span>
          <span />
          <span>Schema Field</span>
          <span className="text-center">Uni</span>
          <span>Uni Value</span>
          {showCustomerMapping && (
            <>
              <span className="text-center">Cust</span>
              <span>Customer Field</span>
            </>
          )}
        </div>

        {/* Mapping rows */}
        <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
          {csvHeaders.map((header) => {
            const row = getRow(header)
            return (
              <div
                key={header}
                className={cn(
                  'grid items-center px-3 py-2 gap-2 text-sm',
                  colClass,
                  row.schemaField ? 'bg-white' : 'bg-amber-50/40',
                )}
              >
                {/* CSV header */}
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className={cn(
                      'inline-block w-1.5 h-1.5 rounded-full shrink-0',
                      row.schemaField ? 'bg-green-500' : 'bg-gray-300',
                    )}
                  />
                  <span className="font-mono text-xs text-gray-700 truncate" title={header}>
                    {header}
                  </span>
                </div>

                {/* Arrow */}
                <span className="text-gray-300 text-center select-none text-xs">→</span>

                {/* Schema field dropdown */}
                <select
                  value={row.schemaField}
                  onChange={(e) => updateRow(header, { schemaField: e.target.value })}
                  className={cn(
                    'w-full text-xs px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary',
                    row.schemaField ? 'border-gray-300' : 'border-amber-300 bg-amber-50',
                  )}
                >
                  <option value="">— skip —</option>
                  <GroupedOptions fields={schemaFields} />
                </select>

                {/* Uni checkbox */}
                <div className="flex justify-center">
                  <input
                    type="checkbox"
                    checked={row.isUni}
                    onChange={(e) =>
                      updateRow(header, { isUni: e.target.checked, uniValue: e.target.checked ? row.uniValue : '' })
                    }
                    title="Mark as unique identifier (uni_params)"
                    className="accent-blue-600 w-3.5 h-3.5 cursor-pointer"
                  />
                </div>

                {/* Uni value input */}
                <div>
                  {row.isUni ? (
                    <input
                      type="text"
                      value={row.uniValue}
                      onChange={(e) => updateRow(header, { uniValue: e.target.value })}
                      placeholder="e.g. email"
                      className="w-full text-xs px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-400 border-blue-300 bg-blue-50"
                    />
                  ) : (
                    <span className="text-xs text-gray-300 italic">—</span>
                  )}
                </div>

                {/* Customer mapping checkbox + field (entity only) */}
                {showCustomerMapping && (
                  <>
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={row.isCustomerMapping}
                        onChange={(e) =>
                          updateRow(header, {
                            isCustomerMapping: e.target.checked,
                            customerField: e.target.checked ? row.customerField : '',
                          })
                        }
                        title="Also map to customer field (customer_field_mappings)"
                        className="accent-purple-600 w-3.5 h-3.5 cursor-pointer"
                      />
                    </div>
                    <div>
                      {row.isCustomerMapping ? (
                        <select
                          value={row.customerField}
                          onChange={(e) => updateRow(header, { customerField: e.target.value })}
                          className={cn(
                            'w-full text-xs px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary',
                            row.customerField ? 'border-gray-300' : 'border-purple-300 bg-purple-50',
                          )}
                        >
                          <option value="">— customer field —</option>
                          <GroupedOptions fields={customerFields} />
                        </select>
                      ) : (
                        <span className="text-xs text-gray-300 italic">—</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mt-1.5 text-xs text-gray-400">
        <span><span className="text-blue-600 font-medium">Uni</span> = unique identifier (uni_params)</span>
        {showCustomerMapping && (
          <span><span className="text-purple-600 font-medium">Cust</span> = link to customer record</span>
        )}
      </div>
    </div>
  )
}
